import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2"; // ✅ import Swal
import { DeleteData, postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function CommissionList() {
  const navigate = useNavigate();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const rowsPerPage = 10;

  // Fetch commissions
  const fetchCommissions = async (page = 0) => {
    setLoading(true);
    try {
      const response = await postData("api/refer-commission/list", {
        page: page + 1,
        limit: rowsPerPage,
      });

      if (response?.success) {
        setCommissions(response?.data || []);
        setTotalItems(
          response?.pagination?.totalItems || response?.data?.length || 0
        );
        setPageCount(response?.pagination?.totalPages || 1);
      } else {
        toast.error(response?.message || "Failed to fetch commissions");
      }
    } catch (error) {
      console.error("Fetch commissions error:", error);
      toast.error("Error fetching commissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions(currentPage);
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Delete Commission List
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await DeleteData("api/refer-commission/delete", {
          ruleId: id,
        });

        if (response?.success) {
          toast.success("Commission deleted successfully");
          fetchCommissions(currentPage); // refresh list
        } else {
          toast.error(response?.message || "Failed to delete commission");
        }
      } catch (error) {
        console.error("Delete commission error:", error);
        toast.error("Error deleting commission");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />

      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <h4 className="fs-18 fw-semibold m-0">Commission List</h4>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/add-commission")}
            >
              <i className="mdi mdi-plus me-1"></i>
              Create Commission
            </button>
          </div>

          {/* Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Category</th>
                            <th>SubCategory</th>
                             <th>Policy Type</th>
                            <th>Levels</th>
                            <th>Percentage</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commissions.length > 0 ? (
                            commissions.map((commission, index) => (
                              <tr key={commission._id}>
                                <td>
                                  {currentPage * rowsPerPage + index + 1}
                                </td>
                                <td>{commission.categoryId?.title || "N/A"}</td>
                                <td>
                                  {commission.subCategoryId?.title || "N/A"}
                                </td>
                                 <td>
                                  {commission.policyType || "N/A"}
                                </td>
                                <td>{commission.levels?.length || 0}</td>
                                <td>
                                  {commission.levels?.map((lvl, i) => (
                                    <span
                                      key={lvl._id}
                                      className="badge bg-light text-dark me-1 mb-1"
                                      style={{ display: "inline-block" }}
                                      title={`Level ${i + 1}: ${
                                        lvl.percentage
                                      }% (${lvl.requiredAgents} agents)`}
                                    >
                                      L{i + 1}: {lvl.percentage}%
                                    </span>
                                  ))}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      title="Edit Commission"
                                      onClick={() =>
                                        navigate(`/add-commission`, {
                                          state: { commission },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>

                                    {/* ✅ Delete with Swal */}
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      title="Delete Commission"
                                      onClick={() => handleDelete(commission._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>

                                    <button
                                      className={`btn btn-sm ${
                                        commission.status === "active"
                                          ? "btn-outline-success"
                                          : "btn-outline-warning"
                                      }`}
                                      title="Toggle Status"
                                      onClick={() =>
                                        handleStatusChange(commission)
                                      }
                                    >
                                      {commission.status === "active" ? (
                                        <i className="mdi mdi-toggle-switch"></i>
                                      ) : (
                                        <i className="mdi mdi-toggle-switch-off"></i>
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No commissions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {pageCount > 1 && (
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            Showing {currentPage * rowsPerPage + 1} to{" "}
                            {Math.min(
                              (currentPage + 1) * rowsPerPage,
                              totalItems
                            )}{" "}
                            of {totalItems} entries
                          </div>
                          <ReactPaginate
                            previousLabel={
                              <i className="mdi mdi-chevron-left"></i>
                            }
                            nextLabel={
                              <i className="mdi mdi-chevron-right"></i>
                            }
                            breakLabel="..."
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                            containerClassName="pagination pagination-rounded justify-content-end mt-3"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
}

export default CommissionList;
